import { useMemo } from 'react'

import { FileUploadResponse, useGetTrackedGamesQuery } from '../../../api/core'
import { useCurrentUserLanguage } from '../../account/hooks/useCurrentUserLanguage'
import { AppType, GamePlatform } from '../../game/types/Game'

const iconUrlOf = (raw: string | FileUploadResponse | undefined): string | undefined => (typeof raw === 'string' ? raw : raw?.url)

/**
 * Game id → icon URL (and → publisher, for the locked-game unlock dialog; and → platform, for the badge on the icon) for the New Adopters faces on the event category and event type
 * pages, for rollup `newGames` entries that arrive without icons (backends predating
 * GR-3245). Unlike the blueprint family page, these pages have no `/blueprints` payload
 * to join one from, so the tracked-games list is used: every new adopter is a tracked game by definition, it
 * covers games outside the user's plan too, and it shares its cache entry with the
 * other Live Event Analytics views.
 */
export function useNewAdopterGameIcons(): {
  iconByGameId: Record<string, string>
  artistByGameId: Record<string, string>
  appTypeByGameId: Record<string, AppType>
  platformsByGameId: Record<string, GamePlatform[]>
  isLoading: boolean
} {
  const userLanguage = useCurrentUserLanguage()
  const { data: trackedGames, isLoading } = useGetTrackedGamesQuery({ userLanguage })

  const { iconByGameId, artistByGameId, appTypeByGameId, platformsByGameId } = useMemo(() => {
    const iconById: Record<string, string> = {}
    const artistById: Record<string, string> = {}
    const appTypeById: Record<string, AppType> = {}
    const platformsById: Record<string, GamePlatform[]> = {}
    for (const { game } of trackedGames ?? []) {
      if (!game?.id) continue
      const icon = iconUrlOf(game.getIcon?.())
      if (icon) iconById[game.id] = icon
      if (game.artist) artistById[game.id] = game.artist
      if (game.appType) appTypeById[game.id] = game.appType
      if (game.platforms?.length) platformsById[game.id] = game.platforms
    }
    return { iconByGameId: iconById, artistByGameId: artistById, appTypeByGameId: appTypeById, platformsByGameId: platformsById }
  }, [trackedGames])

  return { iconByGameId, artistByGameId, appTypeByGameId, platformsByGameId, isLoading }
}
